import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ItemTypeSection.css';
import BookCard from './BookCard';
import { trackEvent } from '../services/analytics';

function ItemTypeSection() {
  const navigate = useNavigate();
  const booksRef = useRef(null);
  const scrollToRef = useRef(null);
  const [itemTypes] = useState([
    { code: 'BK', label: 'New Arrivals', icon: '📚' },
    { code: 'FB', label: 'IITH Faculty authored Books', icon: '👨‍🎓' },
    { code: 'RC', label: 'eBooks', icon: '💻' },
    { code: 'C', label: "Databases & eJournals", icon: '💾' },
  ]);

  const [loading, setLoading] = useState({});
  const [itemCounts, setItemCounts] = useState({});
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [pagination, setPagination] = useState({});

  // Scroll to last-clicked book after selectedBooks renders
  useEffect(() => {
    if (!selectedBooks.length || !scrollToRef.current) return;
    const id = scrollToRef.current;
    scrollToRef.current = null;
    requestAnimationFrame(() => {
      document.getElementById(`book-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [selectedBooks]);

  // Fetch item counts when component mounts
  useEffect(() => {
    // First, restore previous browsing state if it exists
    try {
      const savedState = localStorage.getItem('itemTypeState');
      if (savedState) {
        const { selectedBooks: savedBooks, selectedType: savedType, pagination: savedPagination, lastBiblioId } = JSON.parse(savedState);
        setSelectedBooks(Array.isArray(savedBooks) ? savedBooks : []);
        setSelectedType(savedType || null);
        setPagination(savedPagination || {});
        if (lastBiblioId) scrollToRef.current = String(lastBiblioId);

        // Clear localStorage after restoring
        localStorage.removeItem('itemTypeState');
        localStorage.removeItem('itemTypeScrollPosition');
      }
    } catch (_error) {
      // Ignore malformed or inaccessible localStorage state.
    }

    const fetchCounts = async () => {
      try {
        // Fetch all counts
        const response = await fetch('/api/books/counts');
        const data = await response.json();
        
        // Fetch new arrivals count (last 2 months for BK type)
        const newArrivalsResponse = await fetch('/api/books/counts?newArrivals=true');
        const newArrivalsData = await newArrivalsResponse.json();
        
        // Merge counts - use newArrivals count for BK type, regular counts for others
        const mergedCounts = { ...data };
        if (newArrivalsData.BK) {
          mergedCounts.BK = newArrivalsData.BK;
        }
        
        setItemCounts(mergedCounts);
      } catch (error) {
        console.error('Error fetching item counts:', error);
      }
    };

    fetchCounts();
  }, []);

  const handleItemTypeClick = async (code, label) => {
    // Check if this is an external link button
    if (code === 'RC') {
      trackEvent('ebooks_clicked', { source: 'browse-by-collection' });
      window.open('https://iith.summon.serialssolutions.com/?#!/search?pn=1&ho=t&include.ft.matches=t&l=en', '_blank');
      return;
    }
    if (code === 'C') {
      trackEvent('databases_clicked', { source: 'browse-by-collection' });
      window.open('https://identity.iith.ac.in/', '_blank');
      return;
    }

    if (code === 'BK') {
      trackEvent('new_arrivals_clicked', { source: 'browse-by-collection' });
    }

    if (code === 'FB') {
      trackEvent('faculty_showcase_opened', { source: 'browse-by-collection' });
      navigate('/faculty-showcase');
      return;
    }

    // Reset when switching types
    setSelectedType({ code, label });
    setPagination({});
    
    // Fetch first page
    await fetchBooks(code, label, 0);
  };

  const fetchBooks = async (code, label, offset = 0) => {
    setLoading({ ...loading, [code]: true });
    
    try {
      // Add newArrivals query param for BK (Books) type
      const isNewArrivals = code === 'BK';
      const newArrivalsParam = isNewArrivals ? '&newArrivals=true' : '';
      const response = await fetch(`/api/books/by-type/${code}?limit=30&offset=${offset}${newArrivalsParam}`);
      const data = await response.json();
      
      if (data.books && data.books.length > 0) {
        // Display all books - no deduplication needed since backend groups by biblionumber
        setSelectedBooks(data.books);

        // Scroll to the books list only on a fresh selection (first page), not on pagination.
        if (offset === 0) {
          setTimeout(() => {
            booksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        }
        
        // Update pagination state with page information
        const currentPage = Math.floor(offset / 30) + 1;
        const totalPages = Math.ceil(data.total / 30);
        setPagination({
          ...pagination,
          [code]: {
            offset: offset,
            hasMore: data.books.length === 30 && offset + 30 < data.total,
            canGoBack: offset > 0,
            currentPage: currentPage,
            totalPages: totalPages,
            total: data.total
          }
        });
      } else if (offset === 0) {
        setSelectedBooks([]);
        alert(`No items found for this type`);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      alert('Error fetching items. Please try again.');
    } finally {
      setLoading({ ...loading, [code]: false });
    }
  };

  const handleLoadMore = () => {
    if (selectedType && pagination[selectedType.code]) {
      const nextOffset = pagination[selectedType.code].offset + 30;
      fetchBooks(selectedType.code, selectedType.label, nextOffset);
    }
  };

  const handlePrevious = () => {
    if (selectedType && pagination[selectedType.code]) {
      const currentOffset = pagination[selectedType.code].offset;
      const newOffset = Math.max(0, currentOffset - 30); // Go back by 30 (1 page back)
      fetchBooks(selectedType.code, selectedType.label, newOffset);
    }
  };

  const closeBooks = () => {
    setSelectedBooks([]);
    setSelectedType(null);
  };

  const handleViewDetails = (book) => {
    trackEvent('book_detail_opened', {
      from: 'home-browse',
      biblioId: book.biblio_id,
    });

    // Save browsing state (books + last-clicked book for scroll restoration)
    try {
      localStorage.setItem('itemTypeState', JSON.stringify({
        selectedBooks,
        selectedType,
        pagination,
        lastBiblioId: book.biblio_id,
      }));
    } catch (_error) {
      // Ignore storage quota or private mode errors.
    }
    navigate(`/book/${book.biblio_id}`, { state: { from: 'home-browse' } });
  };

  return (
    <section className="item-type-section">
      <h2>Browse by Collection</h2>
      <div className="item-type-buttons">
        {itemTypes.map((item) => (
          <button
            key={item.code}
            className="item-btn"
            onClick={() => handleItemTypeClick(item.code, item.label)}
            disabled={loading[item.code]}
            title={`${item.label}${item.code === 'RC' || item.code === 'C' ? ' (External Link)' : ' (' + (itemCounts[item.code] || 0) + ' items)'}`}
          >
            <span className="btn-icon">{item.icon}</span>
            <span className="btn-text">{item.label}</span>
            {(item.code === 'RC' || item.code === 'C') && (
              <span className="external-badge">🔗</span>
            )}
            {itemCounts[item.code] > 0 && item.code !== 'RC' && item.code !== 'C' && (
              <span className="item-count">{itemCounts[item.code]}</span>
            )}
            {loading[item.code] && <span className="loading-spinner">⏳</span>}
          </button>
        ))}
        <button
          className="item-btn item-btn--calendar"
          onClick={() => {
            trackEvent('new_arrivals_month_wise_clicked', { source: 'browse-by-collection' });
            navigate('/new-arrivals-month-wise');
          }}
          title="Browse new arrivals by month or date range"
        >
          <span className="btn-icon">📅</span>
          <span className="btn-text">New Arrivals<br/><small>(Month Wise)</small></span>
        </button>
        <button
          className="item-btn item-btn--exhibition"
          onClick={() => {
            trackEvent('virtual_exhibition_clicked', { source: 'browse-by-collection' });
            window.open('https://opac.krc.iith.ac.in/exhibition/', '_blank', 'noopener,noreferrer');
          }}
          title="Open Virtual Exhibition"
        >
          <span className="btn-icon">🏛️</span>
          <span className="btn-text">Virtual Exhibition</span>
          <span className="external-badge">🔗</span>
        </button>
      </div>

      {/* Books Display Section */}
      {selectedBooks.length > 0 && (
        <div className="books-display-container" ref={booksRef}>
          <div className="books-display-header">
            <h3>{selectedType.label} - Showing {selectedBooks.length} books</h3>
            <button className="close-btn" onClick={closeBooks}>✕</button>
          </div>
          <div className="books-grid">
            {selectedBooks.map((book) => (
              <BookCard
                key={book.biblio_id}
                biblioId={book.biblio_id}
                isbn={book.isbn}
                title={book.title}
                author={book.author}
                itemCount={book.item_count}
                onClick={() => handleViewDetails(book)}
              />
            ))}
          </div>

          {/* Pagination - Previous, Page Info, and Next Buttons */}
          <div className="pagination-container">
            {selectedType && pagination[selectedType.code]?.canGoBack && (
              <button 
                className="pagination-btn prev-btn"
                onClick={handlePrevious}
                disabled={loading[selectedType.code]}
              >
                {loading[selectedType.code] ? '⏳' : '← Previous'}
              </button>
            )}
            {selectedType && pagination[selectedType.code]?.currentPage && (
              <span className="pagination-info">
                Page {pagination[selectedType.code].currentPage} of {pagination[selectedType.code].totalPages}
              </span>
            )}
            {selectedType && pagination[selectedType.code]?.hasMore && (
              <button 
                className="pagination-btn next-btn"
                onClick={handleLoadMore}
                disabled={loading[selectedType.code]}
              >
                {loading[selectedType.code] ? '⏳' : 'Next →'}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default ItemTypeSection;
